/**
 * Keyboard adapter. One of the two files in the three-free subsystems the determinism lint
 * exempts for DOM access (ARCHITECTURE.md §4.1).
 *
 * Tracks physical key state in a Set and resolves it to per-player action state on demand. The
 * listeners do nothing but record — no allocation, no game logic — so key handling can never
 * introduce a frame-order dependency.
 */

import { ACTIONS } from '../bindings.js';

export class KeyboardDevice {
  /**
   * @param {object} bindings BindingSet
   * @param {object} [opts]
   * @param {EventTarget} [opts.target] defaults to window
   */
  constructor(bindings, opts = {}) {
    this.bindings = bindings;
    this.target = opts.target || (typeof window !== 'undefined' ? window : null);
    /** @type {Set<string>} physical codes currently down */
    this.down = new Set();
    this.available = !!this.target;
    this._attached = false;
    this._onKeyDown = (e) => this._handleDown(e);
    this._onKeyUp = (e) => this._handleUp(e);
    this._onBlur = () => this.releaseAll();
    this._onVisibility = () => {
      if (typeof document !== 'undefined' && document.hidden) this.releaseAll();
    };
    /** Diagnostic counters surfaced by the dev overlay. */
    this.stats = { downEvents: 0, upEvents: 0, releasedOnBlur: 0 };
  }

  attach(bag) {
    if (!this.available || this._attached) return this;
    // Capture phase so the game sees the key even if a UI element would stop propagation.
    const opts = { capture: true };
    if (bag) {
      bag.listener(this.target, 'keydown', this._onKeyDown, opts);
      bag.listener(this.target, 'keyup', this._onKeyUp, opts);
      bag.listener(this.target, 'blur', this._onBlur);
      if (typeof document !== 'undefined') {
        bag.listener(document, 'visibilitychange', this._onVisibility);
      }
    } else {
      this.target.addEventListener('keydown', this._onKeyDown, opts);
      this.target.addEventListener('keyup', this._onKeyUp, opts);
      this.target.addEventListener('blur', this._onBlur);
    }
    this._attached = true;
    return this;
  }

  _handleDown(e) {
    if (e.repeat) return; // auto-repeat must not re-trigger a press edge
    this.stats.downEvents++;
    this.down.add(e.code);
    // Only swallow keys the game actually uses, so browser shortcuts keep working.
    if (this.bindings.shouldPreventDefault(e.code)) e.preventDefault();
  }

  _handleUp(e) {
    this.stats.upEvents++;
    this.down.delete(e.code);
    if (this.bindings.shouldPreventDefault(e.code)) e.preventDefault();
  }

  /**
   * Releases every held key. Called on blur and tab-hide: without this, alt-tabbing while
   * holding forward leaves the fighter walking forever.
   */
  releaseAll() {
    if (this.down.size > 0) this.stats.releasedOnBlur += this.down.size;
    this.down.clear();
  }

  isDown(code) {
    return this.down.has(code);
  }

  /**
   * Writes this player's raw action state into `out` (a plain object with the ACTIONS keys).
   * @returns {boolean} true if any action is active
   */
  readInto(out, player) {
    const map = this.bindings.keyboard[player];
    let any = false;
    for (let i = 0; i < ACTIONS.length; i++) {
      const action = ACTIONS[i];
      const code = map[action];
      const active = code ? this.down.has(code) : false;
      out[action] = active;
      if (active) any = true;
    }
    return any;
  }

  detach() {
    if (!this._attached || !this.target) return;
    this.target.removeEventListener('keydown', this._onKeyDown, { capture: true });
    this.target.removeEventListener('keyup', this._onKeyUp, { capture: true });
    this.target.removeEventListener('blur', this._onBlur);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this._onVisibility);
    }
    this._attached = false;
    this.down.clear();
  }
}
